/**
 * Активы, которые можно купить: приходят из /public/config (price_configs).
 * Курс, лимиты и сеть задаёт админка — здесь только удобные обёртки.
 */
import { validateTrc20Address, type Trc20Validation } from './tron';

export interface AssetConfig {
  code: string;
  /** Короткий тикер для сумм и полей — из кода актива (usdt -> USDT). */
  ticker: string;
  /** Человеческое название из админки, например «USDT (Tether)». */
  label: string;
  network: string;
  icon_url?: string;
  decimals: number;
  base_price: number;
  min_amount: number;
  max_amount: number;
  transfer_fee: number;
  discount_threshold?: number;
  discount_percent?: number;
  sort_order: number;
}

interface PriceEntry {
  base_price: number;
  min_amount: number;
  max_amount: number;
  discount_threshold?: number;
  discount_percent?: number;
  label?: string;
  description?: string;
  network?: string;
  icon_url?: string;
  decimals?: number;
  transfer_fee?: number;
  sort_order?: number;
}

const DEFAULT_ICONS: Record<string, string> = {
  usdt: '/usdt.svg',
  ton: '/ton.svg',
};

export function buildAssets(prices: Record<string, PriceEntry>): AssetConfig[] {
  return Object.entries(prices)
    .map(([code, p]) => ({
      code,
      ticker: code.toUpperCase(),
      label: p.label || code.toUpperCase(),
      network: p.network || 'TRC-20',
      icon_url: p.icon_url || DEFAULT_ICONS[code],
      decimals: p.decimals ?? 2,
      base_price: p.base_price,
      min_amount: p.min_amount,
      max_amount: p.max_amount,
      transfer_fee: p.transfer_fee ?? 0,
      discount_threshold: p.discount_threshold,
      discount_percent: p.discount_percent,
      sort_order: p.sort_order ?? 0,
    }))
    .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code));
}

/** Быстрая проверка формата — окончательное слово за бэкендом. */
export function looksLikeAddress(address: string, network: string): boolean {
  const addr = (address || '').trim();
  switch ((network || '').toUpperCase()) {
    case 'TRC-20':
      return addr.length === 34 && addr.startsWith('T');
    case 'TON':
      return /^[EU]Q[A-Za-z0-9_-]{46}$/.test(addr);
    case 'BTC':
      return /^(bc1[02-9ac-hj-np-z]{11,71}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(addr);
    case 'ERC-20':
    case 'BEP-20':
      return /^0x[a-fA-F0-9]{40}$/.test(addr);
    case 'SOL':
      return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr);
    default:
      return addr.length >= 20;
  }
}

/** Для TRC-20 доступна полная проверка с контрольной суммой. */
export async function validateAssetAddress(
  address: string,
  network: string
): Promise<Trc20Validation> {
  if ((network || '').toUpperCase() === 'TRC-20') {
    return validateTrc20Address(address);
  }
  return looksLikeAddress(address, network)
    ? { valid: true }
    : { valid: false, reason: `Некорректный адрес для сети ${network}` };
}
